import React from "react";
import { QRCodeCanvas } from "qrcode.react";
import { formatNumber, formatDate } from "@/lib/utils";

interface MoldLabelPrintTemplateProps {
  data?: any;
}

export default function MoldLabelPrintTemplate({ data = {} }: MoldLabelPrintTemplateProps) {
  const moldCode = data.dieCutId || data.moldId || data.code || "DC-0001";
  const moldName = data.name || "Khuôn bế bao bì";
  const productName = data.productName || data.product?.productName || data.product?.name || "Bao bì sản phẩm";
  const customerName = data.customer?.name || data.customerName || "Artpresto";
  const location = data.location || (data.storageRack ? `${data.storageZone || 'KHU_A'} • ${data.storageRack} • ${data.storageShelf || 'TANG_1'}` : "Kho Khuôn");
  const dimensions = (data.dieLength && data.dieWidth) 
    ? `${data.dieLength} x ${data.dieWidth} mm` 
    : (data.size || "Tiêu chuẩn");
  const ups = data.ups ? `${data.ups} con/tờ` : "1 con/tờ";
  const maxImp = data.maxImpressions ? `${formatNumber(data.maxImpressions)} lượt` : "100,000 lượt";
  const usageCount = data.usageCount ? formatNumber(data.usageCount) : "0";

  const qrUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/molds/${data.id || moldCode}`
    : `https://apv.gpems.net/molds/${moldCode}`;

  return (
    <div
      className="bg-white text-slate-900 font-sans p-4 border-2 border-dashed border-slate-300 rounded-lg max-w-[480px] mx-auto print:border-solid print:border-black print:p-2 print:m-0"
      style={{
        WebkitPrintColorAdjust: "exact",
        printColorAdjust: "exact",
      }}
    >
      {/* Header: Company & Form Code */}
      <div className="flex items-center justify-between border-b-2 border-black pb-2 mb-2">
        <div>
          <div className="text-[11px] font-black uppercase tracking-wider text-slate-700">
            APV PACKAGING MES & TOOLING
          </div>
          <div className="text-[9px] text-slate-500">
            Tem Quản Lý Vòng Đời & Vị Trí Gáy Khuôn
          </div>
        </div>
        <div className="text-right">
          <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-slate-100 border border-slate-400 font-bold">
            
          </span>
        </div>
      </div>

      {/* Main Barcode & Code Block */}
      <div className="flex items-center justify-between gap-3 my-2">
        <div className="flex-1 min-w-0">
          <div className="text-xs text-slate-500 font-semibold uppercase">MÃ KHUÔN ĐỘC BẢN:</div>
          <div className="text-2xl font-black font-mono tracking-tight text-indigo-900 truncate" data-testid="mold-label-code">
            {moldCode}
          </div>
          <div className="text-[11px] font-bold text-slate-800 truncate mt-0.5">
            {moldName}
          </div>
          <div className="text-[10px] text-slate-600 truncate mt-0.5">
            SP: <span className="font-semibold">{productName}</span> ({customerName})
          </div>
        </div>

        {/* QR Code */}
        <div className="shrink-0 p-1.5 bg-white border border-slate-300 rounded-md text-center" data-testid="mold-label-qr" data-qr-url={qrUrl}>
          <QRCodeCanvas value={qrUrl} size={84} level="M" />
          <div className="text-[8px] font-mono text-slate-500 mt-0.5">QUÉT TRA CỨU</div>
        </div>
      </div>

      {/* Coordinates Box (Tọa Độ Kho Giá Kệ) */}
      <div className="my-2 p-2 bg-amber-50/80 border-2 border-amber-400 rounded-md">
        <div className="text-[9px] font-bold uppercase text-amber-900 tracking-wider">
          📍 TỌA ĐỘ VỊ TRÍ LƯU KHO (RACK - SHELF - BIN):
        </div>
        <div className="text-sm font-black text-amber-950 font-mono mt-0.5">
          {location} {data.storageBin ? `• Ô: ${data.storageBin}` : ''}
        </div>
      </div>

      {/* Specs Grid */}
      <div className="grid grid-cols-3 gap-1 py-1.5 border-t border-b border-slate-200 text-[10px] font-mono">
        <div>
          <span className="text-slate-500 block text-[9px]">Khổ khuôn:</span>
          <span className="font-bold text-slate-800">{dimensions}</span>
        </div>
        <div>
          <span className="text-slate-500 block text-[9px]">Bố cục (UPS):</span>
          <span className="font-bold text-slate-800">{ups}</span>
        </div>
        <div>
          <span className="text-slate-500 block text-[9px]">Định mức tuổi thọ:</span>
          <span className="font-bold text-slate-800">{maxImp}</span>
        </div>
      </div>

      {/* Footer Info */}
      <div className="flex items-center justify-between pt-2 text-[9px] text-slate-500">
        <div>Đã dập: <strong className="font-mono text-slate-800">{usageCount}</strong> lượt</div>
        <div>Ngày in tem: {formatDate(new Date())}</div>
      </div>
    </div>
  );
}
